import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';

const ACCENT = '#4A7C59';

interface MbuniLink {
  id: string;
  batchNumber: string;
  status: string;
  mbuniKg: string | null;
}

interface Batch {
  id: string;
  batchNumber: string;
  processType: string;
  grossCherryKg: string | null;
  cherryKg: string | null;
  mbuniKg: string | null;
  wasteKg: string | null;
  parentBatchId: string | null;
  status: string;
  centreName: string | null;
  notes: string;
  createdAt: string;
  mbuniLinked?: MbuniLink | null;
}

interface FermentLog {
  id: string;
  tankId: string;
  startTime: string | null;
  endTime: string | null;
  durationHours: string | null;
  waterTempC: string | null;
  phStart: string | null;
  phEnd: string | null;
  notes: string;
}

interface DryingLog {
  id: string;
  bedName: string | null;
  startDate: string;
  endDate: string | null;
  initialMoisturePct: string | null;
  finalMoisturePct: string | null;
  targetMoisturePct: string | null;
  status: string;
  notes: string;
}

interface GradeEntry {
  grade: string;
  quantityKg: string | null;
  screenSize: string | null;
  defectCount: number;
  notes: string;
}

interface MillingRun {
  id: string;
  millingDate: string;
  parchmentKgIn: string | null;
  greenCoffeeKg: string | null;
  outturnPct: string | null;
  notes: string;
  grades: GradeEntry[];
}

const STATUS_LABEL: Record<string, string> = {
  cherry_intake: 'Cherry intake',
  fermentation: 'Fermentation',
  drying: 'Drying',
  milling: 'Milling',
  grading: 'Grading',
  complete: 'Complete',
};

const STATUS_STYLE: Record<string, string> = {
  cherry_intake: 'bg-orange-100 text-orange-700',
  fermentation: 'bg-blue-100 text-blue-700',
  drying: 'bg-yellow-100 text-yellow-700',
  milling: 'bg-purple-100 text-purple-700',
  grading: 'bg-pink-100 text-pink-700',
  complete: 'bg-green-100 text-green-700',
};

type Tab = 'overview' | 'fermentation' | 'drying' | 'milling';

const STANDARD_GRADES = ['AA', 'AB', 'C', 'PB', 'TT', 'UG'];
const MBUNI_GRADES = ['MH', 'ML'];

export const BatchDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [batch, setBatch] = useState<Batch | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  const [fermentLogs, setFermentLogs] = useState<FermentLog[]>([]);
  const [dryingLogs, setDryingLogs] = useState<DryingLog[]>([]);
  const [millingRuns, setMillingRuns] = useState<MillingRun[]>([]);
  const [loading, setLoading] = useState(true);

  // Milling form state
  const [showMillingForm, setShowMillingForm] = useState(false);
  const [millingForm, setMillingForm] = useState({ millingDate: new Date().toISOString().split('T')[0], parchmentKgIn: '', greenCoffeeKg: '', notes: '' });
  const [savingMilling, setSavingMilling] = useState(false);

  // Grading form state (per milling run)
  const [gradingRunId, setGradingRunId] = useState<string | null>(null);
  const [gradeRows, setGradeRows] = useState<{ grade: string; quantityKg: string; screenSize: string; notes: string }[]>([]);
  const [savingGrading, setSavingGrading] = useState(false);

  // Fermentation form state
  const [showFermentForm, setShowFermentForm] = useState(false);
  const [fermentForm, setFermentForm] = useState({ tankId: '', startTime: '', durationHours: '', waterTempC: '', phStart: '', phEnd: '', notes: '' });
  const [savingFerment, setSavingFerment] = useState(false);

  // Drying form state
  const [showDryForm, setShowDryForm] = useState(false);
  const [dryForm, setDryForm] = useState({ startDate: new Date().toISOString().split('T')[0], initialMoisturePct: '', targetMoisturePct: '11.0', notes: '' });
  const [savingDry, setSavingDry] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([
      fetch(`/api/production/batches?id=${id}`, { credentials: 'include' }).then(r => r.json()),
      fetch(`/api/production/fermentation?batchId=${id}`, { credentials: 'include' }).then(r => r.json()),
      fetch(`/api/production/drying?batchId=${id}`, { credentials: 'include' }).then(r => r.json()),
      fetch(`/api/production/milling?batchId=${id}`, { credentials: 'include' }).then(r => r.json()),
    ]).then(([batchData, fermentData, dryData, millingData]) => {
      setBatch(batchData.batches?.[0] ?? null);
      setFermentLogs(fermentData.logs || []);
      setDryingLogs(dryData.logs || []);
      setMillingRuns(millingData.runs || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  const submitFerment = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingFerment(true);
    const res = await fetch('/api/production/fermentation', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ batchId: id, ...fermentForm }),
    });
    if (res.ok) {
      const log = await res.json();
      setFermentLogs(l => [log, ...l]);
      setBatch(b => b ? { ...b, status: b.status === 'cherry_intake' ? 'fermentation' : b.status } : b);
      setShowFermentForm(false);
      setFermentForm({ tankId: '', startTime: '', durationHours: '', waterTempC: '', phStart: '', phEnd: '', notes: '' });
    }
    setSavingFerment(false);
  };

  const submitDry = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingDry(true);
    const res = await fetch('/api/production/drying', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ batchId: id, ...dryForm }),
    });
    if (res.ok) {
      const log = await res.json();
      setDryingLogs(l => [log, ...l]);
      setBatch(b => b ? { ...b, status: ['cherry_intake', 'fermentation'].includes(b.status) ? 'drying' : b.status } : b);
      setShowDryForm(false);
      setDryForm({ startDate: new Date().toISOString().split('T')[0], initialMoisturePct: '', targetMoisturePct: '11.0', notes: '' });
    }
    setSavingDry(false);
  };

  const submitMilling = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingMilling(true);
    const res = await fetch('/api/production/milling', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ batchId: id, ...millingForm }),
    });
    if (res.ok) {
      const run = await res.json();
      setMillingRuns(r => [run, ...r]);
      setBatch(b => b ? { ...b, status: ['cherry_intake', 'fermentation', 'drying'].includes(b.status) ? 'milling' : b.status } : b);
      setShowMillingForm(false);
      setMillingForm({ millingDate: new Date().toISOString().split('T')[0], parchmentKgIn: '', greenCoffeeKg: '', notes: '' });
    }
    setSavingMilling(false);
  };

  const openGradingForm = (runId: string, processType: string) => {
    const grades = processType === 'mbuni' ? MBUNI_GRADES : STANDARD_GRADES;
    setGradeRows(grades.map(g => ({ grade: g, quantityKg: '', screenSize: '', notes: '' })));
    setGradingRunId(runId);
  };

  const submitGrading = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!gradingRunId) return;
    setSavingGrading(true);
    const filled = gradeRows.filter(r => r.quantityKg && parseFloat(r.quantityKg) > 0);
    if (filled.length === 0) { setSavingGrading(false); return; }
    const res = await fetch('/api/production/milling?resource=grading', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ millingRunId: gradingRunId, grades: filled }),
    });
    if (res.ok) {
      const data = await res.json();
      setMillingRuns(runs => runs.map(r =>
        r.id === gradingRunId ? { ...r, grades: [...r.grades, ...(data.grades || [])] } : r
      ));
      setBatch(b => b ? { ...b, status: b.status === 'milling' ? 'grading' : b.status } : b);
      setGradingRunId(null);
    }
    setSavingGrading(false);
  };

  const completeDrying = async (logId: string) => {
    const res = await fetch(`/api/production/drying?id=${logId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ status: 'complete', endDate: new Date().toISOString().split('T')[0] }),
    });
    if (res.ok) {
      setDryingLogs(l => l.map(d => d.id === logId ? { ...d, status: 'complete', endDate: new Date().toISOString().split('T')[0] } : d));
    }
  };

  if (loading) return <div className="py-16 text-center text-sm text-[#6B6B6B]">Loading batch...</div>;
  if (!batch) return (
    <div className="py-16 text-center">
      <p className="text-[#6B6B6B] mb-3">Batch not found</p>
      <Link to="/production/batches" className="text-sm font-medium" style={{ color: ACCENT }}>← Back to batches</Link>
    </div>
  );

  const inputCls = "w-full px-4 py-2.5 border border-[#E8E6E2] rounded-lg text-sm text-[#1A1A1A] bg-white focus:outline-none";
  const focusStyle = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => (e.target.style.boxShadow = `0 0 0 2px ${ACCENT}33`);
  const blurStyle = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => (e.target.style.boxShadow = '');

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <button onClick={() => navigate('/production/batches')} className="text-sm text-[#6B6B6B] hover:text-[#1A1A1A] mb-3 flex items-center gap-1">
          ← Back to batches
        </button>
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-2xl font-bold font-mono text-[#1A1A1A]">{batch.batchNumber}</h2>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[batch.status] || 'bg-neutral-100 text-neutral-600'}`}>
                {STATUS_LABEL[batch.status] || batch.status}
              </span>
              <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-neutral-100 text-neutral-600 capitalize">
                {batch.processType}
              </span>
            </div>
            <div className="flex gap-4 text-xs text-[#6B6B6B]">
              {batch.cherryKg && <span>{parseFloat(batch.cherryKg).toLocaleString()} kg cherry</span>}
              {batch.centreName && <span>{batch.centreName}</span>}
              <span>{new Date(batch.createdAt).toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[#E8E6E2] mb-6">
        {([['overview', 'Overview'], ['fermentation', `Fermentation (${fermentLogs.length})`], ['drying', `Drying (${dryingLogs.length})`], ['milling', `Milling (${millingRuns.length})`]] as [Tab, string][]).map(([t, label]) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
              tab === t ? 'border-[#4A7C59] text-[#4A7C59]' : 'border-transparent text-[#6B6B6B] hover:text-[#1A1A1A]'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Overview tab */}
      {tab === 'overview' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: 'Process type', value: batch.processType },
              { label: 'Status', value: STATUS_LABEL[batch.status] || batch.status },
              { label: 'Collection centre', value: batch.centreName || '—' },
              { label: 'Created', value: new Date(batch.createdAt).toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' }) },
            ].map(({ label, value }) => (
              <div key={label} className="bg-white border border-[#E8E6E2] rounded-xl p-4">
                <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-1">{label}</p>
                <p className="font-semibold text-[#1A1A1A] capitalize">{value}</p>
              </div>
            ))}
          </div>

          {/* Intake equation card */}
          {(batch.grossCherryKg || batch.cherryKg || batch.mbuniKg) && (
            <div className="bg-white border border-[#E8E6E2] rounded-xl p-5">
              <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-4">
                Reception breakdown — W<sub>Total</sub> = W<sub>Primary</sub> + W<sub>Mbuni</sub> + W<sub>Waste</sub>
              </p>
              <div className="space-y-2">
                {batch.grossCherryKg && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#6B6B6B]">Gross intake (W<sub>Total</sub>)</span>
                    <span className="font-bold text-[#1A1A1A]">{parseFloat(batch.grossCherryKg).toLocaleString()} kg</span>
                  </div>
                )}
                <div className="h-px bg-[#E8E6E2]" />
                {batch.cherryKg && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#6B6B6B]">W<sub>Primary</sub> — ripe cherries</span>
                    <span className="font-medium text-[#1A1A1A]">{parseFloat(batch.cherryKg).toLocaleString()} kg</span>
                  </div>
                )}
                {batch.mbuniKg && parseFloat(batch.mbuniKg) > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-amber-700">W<sub>Mbuni</sub> — sorted (under/over-ripes)</span>
                    <span className="font-medium text-amber-700">{parseFloat(batch.mbuniKg).toLocaleString()} kg</span>
                  </div>
                )}
                {batch.wasteKg && parseFloat(batch.wasteKg) > 0 && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#6B6B6B]">W<sub>Waste</sub> — debris</span>
                    <span className="font-medium text-[#6B6B6B]">{parseFloat(batch.wasteKg).toLocaleString()} kg</span>
                  </div>
                )}
                {batch.grossCherryKg && batch.cherryKg && (
                  (() => {
                    const gross = parseFloat(batch.grossCherryKg);
                    const primary = parseFloat(batch.cherryKg);
                    const mbuni = batch.mbuniKg ? parseFloat(batch.mbuniKg) : 0;
                    const waste = batch.wasteKg ? parseFloat(batch.wasteKg) : 0;
                    const yield_pct = gross > 0 ? ((primary / gross) * 100).toFixed(1) : null;
                    return (
                      <div className="pt-2 border-t border-[#E8E6E2] flex items-center justify-between text-xs text-[#A3A3A3]">
                        <span>Primary yield</span>
                        <span className="font-medium text-[#4A7C59]">{yield_pct}% of gross</span>
                      </div>
                    );
                  })()
                )}
              </div>
            </div>
          )}

          {/* Mbuni linked batch */}
          {batch.mbuniLinked && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-600 uppercase tracking-wider font-medium mb-0.5">Linked Mbuni batch</p>
                <p className="font-mono text-sm font-medium text-amber-900">{batch.mbuniLinked.batchNumber}</p>
                {batch.mbuniLinked.mbuniKg && (
                  <p className="text-xs text-amber-700">{parseFloat(batch.mbuniLinked.mbuniKg).toLocaleString()} kg · drying whole</p>
                )}
              </div>
              <a href={`/production/batches/${batch.mbuniLinked.id}`} className="text-xs text-amber-700 hover:text-amber-900 font-medium">
                View →
              </a>
            </div>
          )}

          {/* Mbuni parent link */}
          {batch.parentBatchId && (
            <div className="bg-neutral-50 border border-[#E8E6E2] rounded-xl p-4">
              <p className="text-xs text-[#A3A3A3] mb-1">This is a Mbuni batch — diverted from primary batch</p>
              <a href={`/production/batches/${batch.parentBatchId}`} className="text-sm font-medium" style={{ color: ACCENT }}>
                View primary batch →
              </a>
            </div>
          )}

          {batch.notes && (
            <div className="bg-white border border-[#E8E6E2] rounded-xl p-4">
              <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-1">Notes</p>
              <p className="text-sm text-[#1A1A1A]">{batch.notes}</p>
            </div>
          )}

          {/* Pipeline progress */}
          <div className="bg-white border border-[#E8E6E2] rounded-xl p-5">
            <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-4">Processing pipeline</p>
            <div className="flex items-center gap-2">
              {['cherry_intake', 'fermentation', 'drying', 'milling', 'grading', 'complete'].map((s, i, arr) => {
                const statusIdx = arr.indexOf(batch.status);
                const done = i <= statusIdx;
                const current = i === statusIdx;
                return (
                  <React.Fragment key={s}>
                    <div className="flex flex-col items-center gap-1">
                      <div
                        className={`w-3 h-3 rounded-full border-2 ${
                          current ? 'border-[#4A7C59] bg-[#4A7C59]' : done ? 'border-[#4A7C59] bg-[#4A7C59]/30' : 'border-[#E8E6E2] bg-white'
                        }`}
                      />
                      <span className="text-[9px] text-[#A3A3A3] uppercase tracking-wide whitespace-nowrap">{STATUS_LABEL[s]?.split(' ')[0]}</span>
                    </div>
                    {i < arr.length - 1 && (
                      <div className={`flex-1 h-0.5 ${i < statusIdx ? 'bg-[#4A7C59]' : 'bg-[#E8E6E2]'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Fermentation tab */}
      {tab === 'fermentation' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-[#6B6B6B]">{fermentLogs.length} log{fermentLogs.length !== 1 ? 's' : ''}</p>
            <button onClick={() => setShowFermentForm(true)} className="px-4 py-2 text-sm font-medium text-white rounded-lg" style={{ backgroundColor: ACCENT }}>
              + Add log
            </button>
          </div>

          {showFermentForm && (
            <form onSubmit={submitFerment} className="bg-white border border-[#E8E6E2] rounded-xl p-5 mb-4 space-y-4">
              <h4 className="font-semibold text-[#1A1A1A]">New fermentation log</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-[#6B6B6B] mb-1">Tank ID</label>
                  <input type="text" value={fermentForm.tankId} onChange={e => setFermentForm(f => ({ ...f, tankId: e.target.value }))} placeholder="e.g. Tank 1" className={inputCls} onFocus={focusStyle} onBlur={blurStyle} />
                </div>
                <div>
                  <label className="block text-xs text-[#6B6B6B] mb-1">Start time</label>
                  <input type="datetime-local" value={fermentForm.startTime} onChange={e => setFermentForm(f => ({ ...f, startTime: e.target.value }))} className={inputCls} onFocus={focusStyle} onBlur={blurStyle} />
                </div>
                <div>
                  <label className="block text-xs text-[#6B6B6B] mb-1">Duration (hrs)</label>
                  <input type="number" value={fermentForm.durationHours} onChange={e => setFermentForm(f => ({ ...f, durationHours: e.target.value }))} placeholder="36" step="0.5" className={inputCls} onFocus={focusStyle} onBlur={blurStyle} />
                </div>
                <div>
                  <label className="block text-xs text-[#6B6B6B] mb-1">Water temp (°C)</label>
                  <input type="number" value={fermentForm.waterTempC} onChange={e => setFermentForm(f => ({ ...f, waterTempC: e.target.value }))} placeholder="18" step="0.1" className={inputCls} onFocus={focusStyle} onBlur={blurStyle} />
                </div>
                <div>
                  <label className="block text-xs text-[#6B6B6B] mb-1">pH start</label>
                  <input type="number" value={fermentForm.phStart} onChange={e => setFermentForm(f => ({ ...f, phStart: e.target.value }))} placeholder="6.5" step="0.1" min="0" max="14" className={inputCls} onFocus={focusStyle} onBlur={blurStyle} />
                </div>
                <div>
                  <label className="block text-xs text-[#6B6B6B] mb-1">pH end</label>
                  <input type="number" value={fermentForm.phEnd} onChange={e => setFermentForm(f => ({ ...f, phEnd: e.target.value }))} placeholder="4.0" step="0.1" min="0" max="14" className={inputCls} onFocus={focusStyle} onBlur={blurStyle} />
                </div>
              </div>
              <div>
                <label className="block text-xs text-[#6B6B6B] mb-1">Notes</label>
                <textarea value={fermentForm.notes} onChange={e => setFermentForm(f => ({ ...f, notes: e.target.value }))} rows={2} className={`${inputCls} resize-none`} onFocus={focusStyle} onBlur={blurStyle} />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowFermentForm(false)} className="flex-1 py-2 text-sm text-[#6B6B6B] border border-[#E8E6E2] rounded-lg hover:bg-[#F0EDE8]">Cancel</button>
                <button type="submit" disabled={savingFerment} className="flex-1 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: ACCENT }}>{savingFerment ? 'Saving...' : 'Save log'}</button>
              </div>
            </form>
          )}

          {fermentLogs.length === 0 ? (
            <div className="py-12 text-center text-sm text-[#A3A3A3]">No fermentation logs yet</div>
          ) : (
            <div className="space-y-3">
              {fermentLogs.map(log => (
                <div key={log.id} className="bg-white border border-[#E8E6E2] rounded-xl p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-sm text-[#1A1A1A]">{log.tankId || 'Unnamed tank'}</span>
                    {log.durationHours && <span className="text-xs text-[#6B6B6B]">{log.durationHours} hrs</span>}
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs text-[#6B6B6B]">
                    {log.waterTempC && <span>Temp: {log.waterTempC}°C</span>}
                    {log.phStart && <span>pH start: {log.phStart}</span>}
                    {log.phEnd && <span>pH end: {log.phEnd}</span>}
                  </div>
                  {log.notes && <p className="mt-2 text-xs text-[#A3A3A3]">{log.notes}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Milling tab */}
      {tab === 'milling' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-[#6B6B6B]">{millingRuns.length} milling run{millingRuns.length !== 1 ? 's' : ''}</p>
            <button onClick={() => setShowMillingForm(true)} className="px-4 py-2 text-sm font-medium text-white rounded-lg" style={{ backgroundColor: ACCENT }}>
              + Add milling run
            </button>
          </div>

          {showMillingForm && (
            <form onSubmit={submitMilling} className="bg-white border border-[#E8E6E2] rounded-xl p-5 mb-4 space-y-4">
              <h4 className="font-semibold text-[#1A1A1A]">New milling run</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-[#6B6B6B] mb-1">Milling date <span className="text-red-500">*</span></label>
                  <input type="date" value={millingForm.millingDate} onChange={e => setMillingForm(f => ({ ...f, millingDate: e.target.value }))} required className={inputCls} onFocus={focusStyle} onBlur={blurStyle} />
                </div>
                <div>
                  <label className="block text-xs text-[#6B6B6B] mb-1">Parchment in (kg)</label>
                  <input type="number" value={millingForm.parchmentKgIn} onChange={e => setMillingForm(f => ({ ...f, parchmentKgIn: e.target.value }))} placeholder="e.g. 1500" step="0.1" min="0" className={inputCls} onFocus={focusStyle} onBlur={blurStyle} />
                </div>
                <div>
                  <label className="block text-xs text-[#6B6B6B] mb-1">Green coffee out (kg)</label>
                  <input type="number" value={millingForm.greenCoffeeKg} onChange={e => setMillingForm(f => ({ ...f, greenCoffeeKg: e.target.value }))} placeholder="e.g. 750" step="0.1" min="0" className={inputCls} onFocus={focusStyle} onBlur={blurStyle} />
                </div>
                {millingForm.parchmentKgIn && millingForm.greenCoffeeKg && parseFloat(millingForm.parchmentKgIn) > 0 && (
                  <div className="flex items-end pb-2.5">
                    <div>
                      <p className="text-xs text-[#A3A3A3] mb-0.5">Outturn</p>
                      <p className="text-lg font-bold text-[#4A7C59]">
                        {((parseFloat(millingForm.greenCoffeeKg) / parseFloat(millingForm.parchmentKgIn)) * 100).toFixed(1)}%
                      </p>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-xs text-[#6B6B6B] mb-1">Notes</label>
                <textarea value={millingForm.notes} onChange={e => setMillingForm(f => ({ ...f, notes: e.target.value }))} rows={2} className={`${inputCls} resize-none`} onFocus={focusStyle} onBlur={blurStyle} />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowMillingForm(false)} className="flex-1 py-2 text-sm text-[#6B6B6B] border border-[#E8E6E2] rounded-lg hover:bg-[#F0EDE8]">Cancel</button>
                <button type="submit" disabled={savingMilling} className="flex-1 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: ACCENT }}>{savingMilling ? 'Saving...' : 'Save run'}</button>
              </div>
            </form>
          )}

          {/* Grading form modal */}
          {gradingRunId && (
            <form onSubmit={submitGrading} className="bg-white border border-[#E8E6E2] rounded-xl p-5 mb-4 space-y-4">
              <h4 className="font-semibold text-[#1A1A1A]">Grading results</h4>
              <p className="text-xs text-[#A3A3A3]">Enter kg for each grade. Leave empty to skip.</p>
              <div className="space-y-2">
                {gradeRows.map((row, i) => (
                  <div key={row.grade} className="flex items-center gap-3">
                    <span className="w-12 text-sm font-mono font-bold text-[#1A1A1A]">{row.grade}</span>
                    <input
                      type="number"
                      value={row.quantityKg}
                      onChange={e => setGradeRows(rows => rows.map((r, j) => j === i ? { ...r, quantityKg: e.target.value } : r))}
                      placeholder="0 kg"
                      step="0.1" min="0"
                      className="flex-1 px-3 py-2 border border-[#E8E6E2] rounded-lg text-sm text-[#1A1A1A] bg-white focus:outline-none"
                    />
                    <input
                      type="text"
                      value={row.screenSize}
                      onChange={e => setGradeRows(rows => rows.map((r, j) => j === i ? { ...r, screenSize: e.target.value } : r))}
                      placeholder="Screen"
                      className="w-24 px-3 py-2 border border-[#E8E6E2] rounded-lg text-sm text-[#1A1A1A] bg-white focus:outline-none"
                    />
                  </div>
                ))}
              </div>
              <div className="text-xs text-[#A3A3A3]">
                Total: <span className="font-medium text-[#1A1A1A]">
                  {gradeRows.reduce((s, r) => s + (parseFloat(r.quantityKg) || 0), 0).toLocaleString()} kg
                </span>
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setGradingRunId(null)} className="flex-1 py-2 text-sm text-[#6B6B6B] border border-[#E8E6E2] rounded-lg hover:bg-[#F0EDE8]">Cancel</button>
                <button type="submit" disabled={savingGrading} className="flex-1 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: ACCENT }}>{savingGrading ? 'Saving...' : 'Save grades'}</button>
              </div>
            </form>
          )}

          {millingRuns.length === 0 ? (
            <div className="py-12 text-center text-sm text-[#A3A3A3]">No milling runs yet</div>
          ) : (
            <div className="space-y-4">
              {millingRuns.map(run => (
                <div key={run.id} className="bg-white border border-[#E8E6E2] rounded-xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="font-medium text-sm text-[#1A1A1A]">
                        {new Date(run.millingDate).toLocaleDateString('en-KE', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </p>
                      <div className="flex gap-4 mt-1 text-xs text-[#6B6B6B]">
                        {run.parchmentKgIn && <span>Parchment in: <span className="font-medium text-[#1A1A1A]">{parseFloat(run.parchmentKgIn).toLocaleString()} kg</span></span>}
                        {run.greenCoffeeKg && <span>Green out: <span className="font-medium text-[#1A1A1A]">{parseFloat(run.greenCoffeeKg).toLocaleString()} kg</span></span>}
                        {run.outturnPct && (
                          <span>Outturn: <span className="font-bold text-[#4A7C59]">{parseFloat(run.outturnPct).toFixed(1)}%</span></span>
                        )}
                      </div>
                    </div>
                    {run.grades.length === 0 && !gradingRunId && (
                      <button
                        onClick={() => openGradingForm(run.id, batch?.processType || 'washed')}
                        className="px-3 py-1.5 text-xs font-medium border border-[#E8E6E2] rounded-lg hover:bg-[#F0EDE8]"
                      >
                        + Add grades
                      </button>
                    )}
                  </div>

                  {run.grades.length > 0 && (
                    <div className="border-t border-[#E8E6E2] pt-3 mt-3">
                      <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-2">Grading results</p>
                      <div className="grid grid-cols-3 gap-2">
                        {run.grades.map(g => (
                          <div key={g.grade} className="bg-[#F8F7F5] rounded-lg p-2.5">
                            <p className="text-xs font-mono font-bold text-[#1A1A1A]">{g.grade}</p>
                            <p className="text-sm font-semibold text-[#1A1A1A]">
                              {g.quantityKg ? `${parseFloat(g.quantityKg).toLocaleString()} kg` : '—'}
                            </p>
                            {g.screenSize && <p className="text-xs text-[#A3A3A3]">Screen {g.screenSize}</p>}
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 text-xs text-[#A3A3A3]">
                        Total graded: <span className="font-medium text-[#1A1A1A]">
                          {run.grades.reduce((s, g) => s + (g.quantityKg ? parseFloat(g.quantityKg) : 0), 0).toLocaleString()} kg
                        </span>
                      </div>
                    </div>
                  )}

                  {run.notes && <p className="text-xs text-[#A3A3A3] mt-2">{run.notes}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Drying tab */}
      {tab === 'drying' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-[#6B6B6B]">{dryingLogs.length} log{dryingLogs.length !== 1 ? 's' : ''}</p>
            <button onClick={() => setShowDryForm(true)} className="px-4 py-2 text-sm font-medium text-white rounded-lg" style={{ backgroundColor: ACCENT }}>
              + Start drying
            </button>
          </div>

          {showDryForm && (
            <form onSubmit={submitDry} className="bg-white border border-[#E8E6E2] rounded-xl p-5 mb-4 space-y-4">
              <h4 className="font-semibold text-[#1A1A1A]">New drying log</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-[#6B6B6B] mb-1">Start date <span className="text-red-500">*</span></label>
                  <input type="date" value={dryForm.startDate} onChange={e => setDryForm(f => ({ ...f, startDate: e.target.value }))} required className={inputCls} onFocus={focusStyle} onBlur={blurStyle} />
                </div>
                <div>
                  <label className="block text-xs text-[#6B6B6B] mb-1">Initial moisture (%)</label>
                  <input type="number" value={dryForm.initialMoisturePct} onChange={e => setDryForm(f => ({ ...f, initialMoisturePct: e.target.value }))} placeholder="55" step="0.1" className={inputCls} onFocus={focusStyle} onBlur={blurStyle} />
                </div>
                <div>
                  <label className="block text-xs text-[#6B6B6B] mb-1">Target moisture (%)</label>
                  <input type="number" value={dryForm.targetMoisturePct} onChange={e => setDryForm(f => ({ ...f, targetMoisturePct: e.target.value }))} placeholder="11" step="0.1" className={inputCls} onFocus={focusStyle} onBlur={blurStyle} />
                </div>
              </div>
              <div>
                <label className="block text-xs text-[#6B6B6B] mb-1">Notes</label>
                <textarea value={dryForm.notes} onChange={e => setDryForm(f => ({ ...f, notes: e.target.value }))} rows={2} className={`${inputCls} resize-none`} onFocus={focusStyle} onBlur={blurStyle} />
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowDryForm(false)} className="flex-1 py-2 text-sm text-[#6B6B6B] border border-[#E8E6E2] rounded-lg hover:bg-[#F0EDE8]">Cancel</button>
                <button type="submit" disabled={savingDry} className="flex-1 py-2 text-sm font-medium text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: ACCENT }}>{savingDry ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          )}

          {dryingLogs.length === 0 ? (
            <div className="py-12 text-center text-sm text-[#A3A3A3]">No drying logs yet</div>
          ) : (
            <div className="space-y-3">
              {dryingLogs.map(log => {
                const days = log.startDate && log.endDate
                  ? Math.round((new Date(log.endDate).getTime() - new Date(log.startDate).getTime()) / 86400000)
                  : log.startDate
                  ? Math.round((Date.now() - new Date(log.startDate).getTime()) / 86400000)
                  : null;

                return (
                  <div key={log.id} className="bg-white border border-[#E8E6E2] rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {log.bedName && <span className="font-medium text-sm text-[#1A1A1A]">{log.bedName}</span>}
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${log.status === 'complete' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                          {log.status === 'complete' ? 'Complete' : 'Active'}
                        </span>
                      </div>
                      {log.status === 'active' && (
                        <button onClick={() => completeDrying(log.id)} className="text-xs text-[#6B6B6B] hover:text-[#4A7C59] font-medium">Mark complete</button>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-xs text-[#6B6B6B]">
                      <span>Start: {new Date(log.startDate).toLocaleDateString('en-KE', { day: '2-digit', month: 'short' })}</span>
                      {log.endDate && <span>End: {new Date(log.endDate).toLocaleDateString('en-KE', { day: '2-digit', month: 'short' })}</span>}
                      {days !== null && <span>{days} day{days !== 1 ? 's' : ''}</span>}
                      {log.initialMoisturePct && <span>Start: {log.initialMoisturePct}%</span>}
                      {log.finalMoisturePct && <span>Final: {log.finalMoisturePct}%</span>}
                      {log.targetMoisturePct && <span>Target: {log.targetMoisturePct}%</span>}
                    </div>
                    {log.notes && <p className="mt-2 text-xs text-[#A3A3A3]">{log.notes}</p>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
